import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { FaSync, FaMapMarkerAlt, FaCalendarAlt, FaStar, FaCircle, FaUserTie, FaQrcode, FaArrowRight } from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';


const STATUS_CFG = {
  available: { label: 'Boş',    color: '#10B981', bg: '#D1FAE5' },
  reserved:  { label: 'Rezerv', color: '#F59E0B', bg: '#FEF3C7' },
  occupied:  { label: 'Dolu',   color: '#EF4444', bg: '#FEE2E2' },
};

function today() {
  return new Date().toISOString().split('T')[0];
}

function occupancyColor(pct) {
  if (pct >= 80) return '#EF4444';
  if (pct >= 50) return '#F59E0B';
  return '#10B981';
}

export default function ChainOverviewPage() {
  const navigate                              = useNavigate();
  const { business }                          = useAuth();
  const { locations, isOwner, setActiveLocation, setAdminView } = useLocation();

  const [branchData, setBranchData]   = useState({});
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [managers, setManagers]       = useState([]);

  // ── Load per-branch stats ─────────────────────────────────────────────────
  const loadBranchStats = useCallback(async () => {
    if (!business?.id || !locations.length) { setLoading(false); return; }
    setLoading(true);

    const todayStr = today();
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const results = {};

    await Promise.all(
      locations.map(async (loc) => {
        try {
          const [tablesRes, bookingsRes, monthCheckInsRes, monthBookingsRes] = await Promise.all([
            supabase.from('tables').select('status')
              .eq('business_id', business.id).eq('business_location_id', loc.id),

            supabase.from('flash_bookings').select('id, status')
              .eq('business_id', business.id)
              .eq('location_id', loc.id)
              .eq('reservation_date', todayStr),

            supabase.from('check_ins').select('id', { count: 'exact', head: true })
              .eq('business_id', loc.id)
              .gte('created_at', monthStart),

            supabase.from('flash_bookings').select('id', { count: 'exact', head: true })
              .eq('business_id', business.id)
              .eq('location_id', loc.id)
              .gte('created_at', monthStart),
          ]);

          const tables = tablesRes.data || [];
          const tableCounts = { available: 0, reserved: 0, occupied: 0, total: tables.length };
          tables.forEach(t => { if (tableCounts[t.status] !== undefined) tableCounts[t.status]++; });

          const bookings       = bookingsRes.data || [];
          const pendingCount   = bookings.filter(b => b.status === 'pending').length;
          const confirmedCount = bookings.filter(b => b.status === 'confirmed' || b.status === 'approved' || b.status === 'accepted').length;

          results[loc.id] = {
            tableCounts, pendingCount, confirmedCount,
            monthCheckIns: monthCheckInsRes.count || 0,
            monthBookings: monthBookingsRes.count  || 0,
          };
        } catch {
          results[loc.id] = {
            tableCounts: { available: 0, reserved: 0, occupied: 0, total: 0 },
            pendingCount: 0, confirmedCount: 0,
            monthCheckIns: 0, monthBookings: 0,
          };
        }
      })
    );

    setBranchData(results);
    setLastRefresh(new Date());
    setLoading(false);
  }, [business, locations]);

  useEffect(() => { loadBranchStats(); loadManagers(); }, [loadBranchStats]);

  const loadManagers = async () => {
    if (!business?.id) return;
    const { data } = await supabase
      .from('business_users')
      .select('location_id, user_email, role')
      .eq('business_id', business.id);
    setManagers(data || []);
  };

  // ── Aggregate totals ──────────────────────────────────────────────────────
  const totals = locations.reduce((acc, loc) => {
    const d = branchData[loc.id];
    acc.totalPending    += d?.pendingCount ?? 0;
    acc.totalConfirmed  += d?.confirmedCount ?? 0;
    acc.openBranches    += loc.is_open ? 1 : 0;
    acc.totalCapacity   += loc.total_capacity || 0;
    acc.totalOccupancy  += loc.current_occupancy || 0;
    acc.totalTables     += d?.tableCounts?.total ?? 0;
    acc.tablesOccupied  += d?.tableCounts?.occupied ?? 0;
    acc.tablesReserved  += d?.tableCounts?.reserved ?? 0;
    acc.totalMonthCheckIns += d?.monthCheckIns ?? 0;
    acc.totalMonthBookings += d?.monthBookings ?? 0;
    return acc;
  }, { totalPending: 0, totalConfirmed: 0, openBranches: 0, totalCapacity: 0, totalOccupancy: 0, totalTables: 0, tablesOccupied: 0, tablesReserved: 0, totalMonthCheckIns: 0, totalMonthBookings: 0 });

  const overallOccupancyPct = totals.totalCapacity
    ? Math.round((totals.totalOccupancy / totals.totalCapacity) * 100)
    : 0;

  if (!isOwner) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        Bu səhifəyə yalnız sahiblər giriş edə bilər.
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* ── Page Header ── */}
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.pageTitle}>🏪 Filial İcmalı</h1>
          <p style={s.pageSub}>
            {business?.name} · {locations.length} filial
            {lastRefresh && (
              <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>
                · Son yenilənmə: {lastRefresh.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <button style={s.refreshBtn} onClick={loadBranchStats} disabled={loading}>
          <FaSync style={{ marginRight: 6, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Yüklənir...' : 'Yenilə'}
        </button>
      </div>

      {/* ── Top Summary Cards ── */}
      <div style={s.summaryGrid}>
        <SummaryCard
          icon="🏪" label="Açıq Filiallar"
          value={`${totals.openBranches} / ${locations.length}`}
          color="#10B981"
        />
        <SummaryCard
          icon="👥" label="Ümumi Doluluq"
          value={`${overallOccupancyPct}%`}
          sub={`${totals.totalOccupancy} / ${totals.totalCapacity} nəfər`}
          color={occupancyColor(overallOccupancyPct)}
        />
        <SummaryCard
          icon="🍽️" label="Masalar (Dolu/Rezerv)"
          value={`${totals.tablesOccupied} / ${totals.totalTables}`}
          sub={`${totals.tablesReserved} rezerv`}
          color="#F59E0B"
        />
        <SummaryCard
          icon="📅" label="Bu günkü Rezervasiyalar"
          value={totals.totalConfirmed + totals.totalPending}
          sub={totals.totalPending > 0 ? `${totals.totalPending} gözləyir` : 'Gözləyən yoxdur'}
          color="#E63950"
          highlight={totals.totalPending > 0}
        />
        <SummaryCard
          icon="📲" label="Bu ay Check-in (cəmi)"
          value={totals.totalMonthCheckIns}
          sub={`${totals.totalMonthBookings} rezervasiya`}
          color="#667EEA"
        />
      </div>

      {/* ── Branch Comparison Chart ── */}
      {!loading && locations.length > 1 && (() => {
        const chartData = locations.map(loc => {
          const d = branchData[loc.id];
          const shortName = loc.name.length > 14 ? loc.name.slice(0, 13) + '…' : loc.name;
          return {
            name: shortName,
            'Rezervasiya': d?.monthBookings  ?? 0,
            'Check-in':    d?.monthCheckIns  ?? 0,
          };
        });
        return (
          <div style={s.chartCard}>
            <div style={s.chartTitle}>📊 Bu ay filiallar üzrə müqayisə</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: -16, bottom: 4 }} barCategoryGap="28%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 13 }}
                  cursor={{ fill: '#F8FAFC' }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                <Bar dataKey="Rezervasiya" fill="#E63950" radius={[6, 6, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Check-in"    fill="#667EEA" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* ── Branch Grid ── */}
      {loading && Object.keys(branchData).length === 0 ? (
        <div style={s.loadingState}>
          <div style={s.spinner} />
          <p style={{ color: '#888', marginTop: 16 }}>Filial məlumatları yüklənir...</p>
        </div>
      ) : (
        <div style={s.branchGrid}>
          {locations.map(loc => {
            const d = branchData[loc.id] || { tableCounts: { available: 0, reserved: 0, occupied: 0, total: 0 }, pendingCount: 0, confirmedCount: 0, monthCheckIns: 0, monthBookings: 0 };

            return (
              <div key={loc.id} style={s.branchCard}>
                {/* Card Header */}
                <div style={s.cardHeader}>
                  <div style={s.cardHeaderLeft}>
                    <div style={{ ...s.statusDot, background: loc.is_open ? '#10B981' : '#9CA3AF' }} />
                    <div>
                      <div style={s.branchName}>{loc.name}</div>
                      <div style={s.branchAddr}>
                        <FaMapMarkerAlt style={{ fontSize: 10, marginRight: 4, color: '#aaa' }} />
                        {loc.city}{loc.address ? ` · ${loc.address}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ ...s.openBadge, background: loc.is_open ? '#D1FAE5' : '#F3F4F6', color: loc.is_open ? '#065F46' : '#6B7280' }}>
                    {loc.is_open ? 'Açıq' : 'Bağlı'}
                  </div>
                </div>

                {/* Table Status */}
                <div style={s.section}>
                  <div style={s.sectionLabel}>🍽️ Masalar ({d.tableCounts.total})</div>
                  {d.tableCounts.total > 0 ? (
                    <div style={s.tableStatusRow}>
                      {Object.entries(STATUS_CFG).map(([key, cfg]) => (
                        <div key={key} style={{ ...s.tableChip, background: cfg.bg, color: cfg.color }}>
                          <FaCircle style={{ fontSize: 7, marginRight: 4 }} />
                          {d.tableCounts[key] || 0} {cfg.label}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#ccc', marginTop: 4 }}>Floor plan qurulmayıb</div>
                  )}
                </div>

                {/* Stats Row */}
                <div style={s.statsRow}>
                  <div style={s.statItem}>
                    <FaCalendarAlt style={{ color: d.pendingCount > 0 ? '#E63950' : '#9CA3AF', fontSize: 13 }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: d.pendingCount > 0 ? '#E63950' : '#1a1a2e' }}>
                        {d.confirmedCount + d.pendingCount}
                      </div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                        Bu gün rezerv
                        {d.pendingCount > 0 && <span style={{ color: '#E63950', fontWeight: 700 }}> · {d.pendingCount} gözləyir</span>}
                      </div>
                    </div>
                  </div>
                  <div style={s.statDivider} />
                  <div style={s.statItem}>
                    <FaStar style={{ color: '#FBBF24', fontSize: 13 }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>
                        {loc.rating > 0 ? Number(loc.rating).toFixed(1) : '—'}
                      </div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>Reytinq</div>
                    </div>
                  </div>
                </div>

                {/* Monthly Stats */}
                <div style={s.monthlyRow}>
                  <div style={s.monthlyItem}>
                    <FaQrcode style={{ color: '#E63950', fontSize: 12 }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>{d.monthCheckIns ?? 0}</div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>Bu ay check-in</div>
                    </div>
                  </div>
                  <div style={s.statDivider} />
                  <div style={s.monthlyItem}>
                    <FaCalendarAlt style={{ color: '#667EEA', fontSize: 12 }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>{d.monthBookings ?? 0}</div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>Bu ay rezerv</div>
                    </div>
                  </div>
                </div>

                {/* Manager Info */}
                {(() => {
                  const mgr = managers.find(m => m.location_id === loc.id && m.role === 'manager');
                  return (
                    <div style={s.managerRow}>
                      <FaUserTie style={{ color: mgr ? '#10B981' : '#9CA3AF', fontSize: 12, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: mgr ? '#374151' : '#9CA3AF', fontWeight: mgr ? 600 : 400 }}>
                        {mgr ? mgr.user_email : 'Müdir təyin edilməyib'}
                      </span>
                    </div>
                  );
                })()}

                {/* Navigate to branch */}
                <button
                  onClick={() => { setAdminView(false); setActiveLocation(loc); navigate('/dashboard'); }}
                  style={s.manageBranchBtn}
                >
                  <span>{loc.name} — İdarə Et</span>
                  <FaArrowRight style={{ fontSize: 12 }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Summary Card ──────────────────────────────────────────────────────────────
function SummaryCard({ icon, label, value, sub, color, highlight }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      padding: '18px 20px',
      boxShadow: highlight
        ? `0 0 0 2px ${color}40, 0 2px 12px rgba(0,0,0,0.06)`
        : '0 2px 12px rgba(0,0,0,0.06)',
      border: highlight ? `1.5px solid ${color}50` : '1.5px solid transparent',
      flex: 1,
      minWidth: 150,
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#1a1a2e', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{sub}</div>}
      <div style={{ fontSize: 12, color: '#64748B', marginTop: 6, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: { padding: '28px 24px', maxWidth: 1100, margin: '0 auto' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16 },
  pageTitle: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  pageSub: { margin: '4px 0 0', color: '#888', fontSize: 14 },
  refreshBtn: {
    display: 'flex', alignItems: 'center',
    padding: '9px 18px', borderRadius: 10, border: 'none',
    background: '#1a1a2e', color: '#fff', fontWeight: 700,
    fontSize: 13, cursor: 'pointer', flexShrink: 0,
  },
  summaryGrid: {
    display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28,
  },
  branchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  branchCard: {
    background: '#fff',
    borderRadius: 16,
    border: '1.5px solid #F1F5F9',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  cardHeaderLeft: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  statusDot: { width: 10, height: 10, borderRadius: '50%', marginTop: 4, flexShrink: 0 },
  branchName: { fontSize: 15, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.3 },
  branchAddr: { fontSize: 12, color: '#9CA3AF', marginTop: 2, display: 'flex', alignItems: 'center' },
  openBadge: { fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, flexShrink: 0, letterSpacing: 0.3 },

  section: { display: 'flex', flexDirection: 'column', gap: 4 },
  sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionLabel: { fontSize: 12, fontWeight: 600, color: '#64748B' },
  progressBg: { height: 7, borderRadius: 4, background: '#F1F5F9', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: '0.4s ease' },

  tableStatusRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  tableChip: {
    display: 'flex', alignItems: 'center',
    fontSize: 11, fontWeight: 700,
    padding: '3px 9px', borderRadius: 20,
  },

  statsRow: {
    display: 'flex', alignItems: 'center', gap: 0,
    background: '#F8FAFC', borderRadius: 12, padding: '12px 16px',
  },
  statItem: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  statDivider: { width: 1, height: 32, background: '#E5E7EB', flexShrink: 0, margin: '0 8px' },

  monthlyRow: {
    display: 'flex', alignItems: 'center', gap: 0,
    background: '#F0FDF4', borderRadius: 12, padding: '10px 16px',
  },
  monthlyItem: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  managerRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#F8FAFC', borderRadius: 10, padding: '9px 14px',
    border: '1px solid #E5E7EB',
  },
  manageBranchBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', padding: '10px 16px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d5e 100%)',
    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(26,26,46,0.2)',
    transition: 'opacity 0.15s',
  },
  chartCard: {
    background: '#fff', borderRadius: 16, border: '1.5px solid #F1F5F9',
    padding: '20px 24px', marginBottom: 28,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 18 },
  loadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' },
  spinner: {
    width: 36, height: 36, borderRadius: '50%',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #E63950',
    animation: 'spin 0.8s linear infinite',
  },
};
